/**
 * Markdown → DocumentChild[] converter
 * ─────────────────────────────────────
 * Converts a markdown string (or a Tiptap doc) into the contract's
 * `DocumentChild[]` so edited SOW narrative can be injected into a template
 * via a `contentBlock` node. Reuses the proven markdown parser in
 * `@protoss/shared` (`parseContentToTiptap`) rather than re-implementing one.
 *
 * Supported nodes: headings, paragraphs, bullet/ordered lists (incl. nesting),
 * blockquotes, code blocks, and simple tables. Inline marks: bold, italic.
 */
import { parseContentToTiptap } from '@protoss/shared';
import { evenColumnWidths } from './schema/contract.js';
import { DEFAULT_HIGHLIGHT_COLOR } from './sow-highlight-colors.js';
/**
 * Space (twips) after each body paragraph so consecutive narrative paragraphs read as
 * separate blocks (matching the SOW template's blank-line gap). Applied to top-level
 * paragraphs ONLY - list items, table cells, and headings stay tight.
 */
const BODY_PARAGRAPH_SPACE_AFTER = 160;
const HEADING_BY_LEVEL = {
    1: 'Heading1',
    2: 'Heading2',
    3: 'Heading3',
    4: 'Heading4',
    5: 'Heading5',
    6: 'Heading6',
};
function hasMark(node, markType) {
    return Array.isArray(node.marks) && node.marks.some((m) => m?.type === markType);
}
/** Read a string attr off a node's mark (e.g. the `href` of a `link` mark), or undefined. */
function markAttr(node, markType, attr) {
    if (!Array.isArray(node.marks))
        return undefined;
    const mark = node.marks.find((m) => m?.type === markType);
    const v = mark?.attrs?.[attr];
    return typeof v === 'string' && v.trim() ? v : undefined;
}
/** Convert a node's inline children (text + hardBreak) into TextRun definitions. */
function inlineRuns(nodes) {
    const runs = [];
    let pendingBreak = 0;
    const visit = (list) => {
        for (const node of list ?? []) {
            if (node.type === 'text') {
                const run = { text: node.text ?? '' };
                if (hasMark(node, 'bold'))
                    run.bold = true;
                if (hasMark(node, 'italic'))
                    run.italics = true;
                if (hasMark(node, 'underline'))
                    run.underline = true;
                // A user highlight mark carries to the contract run's `highlight` (a color NAME the
                // renderers map via the highlight-color registry). Single-color today -> the default.
                if (hasMark(node, 'highlight'))
                    run.highlight = DEFAULT_HIGHLIGHT_COLOR;
                // A `link` mark (markdown [text](url) / tiptap Link extension) carries to run.link so
                // the renderers emit a real hyperlink (docx ExternalHyperlink / Google textStyle.link /
                // web <a>). Without this, markdown links in narrative + contentBlock defaults render as
                // plain text.
                const href = markAttr(node, 'link', 'href');
                // A "#name" href is an in-document cross-reference -> run.anchor (docx
                // InternalHyperlink to the paragraph bookmarked "name"), not an external URL.
                if (href) {
                    if (href.startsWith('#'))
                        run.anchor = href.slice(1);
                    else
                        run.link = href;
                }
                if (pendingBreak > 0) {
                    run.break = pendingBreak;
                    pendingBreak = 0;
                }
                runs.push(run);
            }
            else if (node.type === 'hardBreak') {
                pendingBreak += 1;
            }
            else if (node.type === 'protossToken') {
                // The editor's token chip serializes back to its literal `{{protoss:...}}`.
                const token = String(node.attrs?.token ?? '');
                if (token) {
                    const run = { text: `{{${token}}}` };
                    // Keep the surrounding emphasis, so a token inside bold text (e.g. a milestone
                    // amount in "**Plan phase complete - 25% ({{...}})**") renders bold like its line.
                    if (hasMark(node, 'bold'))
                        run.bold = true;
                    if (hasMark(node, 'italic'))
                        run.italics = true;
                    if (hasMark(node, 'underline'))
                        run.underline = true;
                    if (hasMark(node, 'highlight'))
                        run.highlight = DEFAULT_HIGHLIGHT_COLOR;
                    if (pendingBreak > 0) {
                        run.break = pendingBreak;
                        pendingBreak = 0;
                    }
                    runs.push(run);
                }
            }
            else if (Array.isArray(node.content)) {
                visit(node.content);
            }
        }
    };
    visit(nodes);
    if (pendingBreak > 0)
        runs.push({ text: '', break: pendingBreak });
    return runs.length > 0 ? runs : [{ text: '' }];
}
let ordinalCounter = 0;
/** Heading-level shift for the current conversion (e.g. -1 so narrative sub-headings render one level below their section's contract heading). Set per markdownToDocumentChildren call. */
let headingLevelOffset = 0;
/** Color (hex or `{{token}}`) applied to heading runs for the current conversion, so narrative sub-headings match the contract headings' brand color. Empty = inherit the docx style default. Set per markdownToDocumentChildren call. */
let headingColor = '';
/** Deepest ADJUSTED level `headingColor` applies to; 0 = no cap (every level), the original behavior. Set per markdownToDocumentChildren call. */
let headingColorMaxLevel = 0;
/** Explicit color past `headingColorMaxLevel`; empty = inherit the heading style. */
let headingColorAfterMaxLevel = '';
/** Deepest ADJUSTED level forced bold; 0 = no cap (every level), the original behavior. */
let headingBoldMaxLevel = 0;
/** Explicit size past `headingColorMaxLevel`; 0 = inherit the heading style. */
let headingSizeAfterMaxLevel = 0;
/** Background fill (hex or `{{token}}`) for narrative heading paragraphs - turns sub-headings into filled brand-color bars. Empty = no fill. Set per call. */
let headingShading = '';
/** Background fill (hex or `{{token}}`) for static markdown-table HEADER cells (e.g. the SOW template's workspace-color header row). Empty = no fill. Set per call. */
let tableHeaderFill = '';
/** Text color (hex or `{{token}}`) for static markdown-table HEADER cell runs (white on the brand fill). Empty = inherit. Set per call. */
let tableHeaderColor = '';
/** True only while rendering an uploaded change-request document. */
let sourceDocumentFormatting = false;
function convertListItems(listNode, kind, level, ordinalKey) {
    const out = [];
    for (const item of listNode.content ?? []) {
        if (item.type !== 'listItem')
            continue;
        for (const block of item.content ?? []) {
            if (block.type === 'bulletList') {
                out.push(...convertListItems(block, 'bullet', level + 1, ordinalKey));
            }
            else if (block.type === 'orderedList') {
                ordinalCounter += 1;
                out.push(...convertListItems(block, 'ordered', level + 1, `ol-${ordinalCounter}`));
            }
            else {
                // paragraph (or other block) → a single list-item line
                out.push({
                    type: 'paragraph',
                    children: inlineRuns(block.content),
                    list: { kind, level, ...(kind === 'ordered' ? { ordinalKey } : {}) },
                });
            }
        }
    }
    return out;
}
/**
 * Close a top-level list block: items stay tight, but the LAST item gets the body
 * paragraph gap so the following block doesn't start flush against the list.
 */
function endListBlock(items) {
    const last = items[items.length - 1];
    if (last)
        last.spacing = { ...last.spacing, after: BODY_PARAGRAPH_SPACE_AFTER };
    return items;
}
function convertTable(node) {
    const rows = (node.content ?? [])
        .filter((r) => r.type === 'tableRow')
        .map((row) => {
        const isHeader = (row.content ?? []).some((cell) => cell.type === 'tableHeader');
        const styleHeader = isHeader && (tableHeaderFill !== '' || tableHeaderColor !== '');
        return {
            children: (row.content ?? []).map((cell) => ({
                children: (cell.content ?? []).map((block) => ({
                    type: 'paragraph',
                    children: styleHeader && tableHeaderColor
                        ? inlineRuns(block.content).map((r) => 'text' in r ? { ...r, color: tableHeaderColor } : r)
                        : inlineRuns(block.content),
                })),
                margins: { top: 60, bottom: 60, left: 80, right: 80 },
                ...(styleHeader && tableHeaderFill ? { shading: { fill: tableHeaderFill } } : {}),
            })),
            tableHeader: isHeader,
        };
    });
    // GFM tables carry no per-column widths; without an explicit `columnWidths` the
    // table collapses to 1-char columns on Google Docs export. Give it an even split,
    // counting columns the same way the renderers do (first row, incl. column spans).
    const colCount = rows[0]?.children.reduce((n, c) => n + (c.columnSpan ?? 1), 0) || 1;
    return {
        type: 'table',
        rows,
        width: { size: 100, type: 'pct' },
        columnWidths: evenColumnWidths(colCount),
        borders: {
            top: { style: 'single', size: 2, color: 'E2E8F0' },
            bottom: { style: 'single', size: 2, color: 'E2E8F0' },
            left: { style: 'single', size: 2, color: 'E2E8F0' },
            right: { style: 'single', size: 2, color: 'E2E8F0' },
            insideHorizontal: { style: 'single', size: 2, color: 'E2E8F0' },
            insideVertical: { style: 'single', size: 2, color: 'E2E8F0' },
        },
    };
}
const KNOWN_DEV_STORY_SECTION_HEADINGS = new Set([
    'description',
    'solution goals',
    'general specification',
    'general specifications',
    'assumptions/risks/exclusions',
    'assumptions, risks, exclusions',
    'assumptions/risks',
    'assumptions',
    'risks',
    'exclusions',
    'user acceptance criteria',
    'acceptance criteria',
]);
function isUserAcceptanceHeading(text) {
    const clean = text
        .trim()
        .toLowerCase()
        .replace(/[:\s]+$/, '');
    return clean === 'user acceptance criteria' || clean === 'acceptance criteria';
}
function isUseCaseHeading(text) {
    const clean = text.trim().replace(/^[\*\_\s]+/, '').replace(/[\*\_\s]+$/, '');
    return /^use\s+case\s*\d*[:\.\s]/i.test(clean);
}
function isKnownSectionHeading(text) {
    const clean = text
        .trim()
        .toLowerCase()
        .replace(/^[#\*\s]+/, '')
        .replace(/[:\s]+$/, '');
    return KNOWN_DEV_STORY_SECTION_HEADINGS.has(clean);
}
/**
 * Fixed SOW subsection labels that render as sub-sub-headers (bold grey body-size
 * labels) at ANY heading level. Level alone can't identify them: the same default
 * passes through with different heading offsets per pipeline (contentBlock render,
 * editor merge at -1, saved-narrative export), so the label treatment is keyed by
 * name to stay identical everywhere.
 */
const SUBSECTION_LABELS = new Set([
    'meals and entertainment',
    'airfare',
    'lodging',
    'ground transportation',
    "included services (upon client's request)",
    'out of scope',
    'resources and responsibilities',
    'process overview',
    'included project management activities',
]);
function isSubsectionLabel(text) {
    const clean = text
        .trim()
        .replace(/^[\#\*\_\s]+/, '')
        .replace(/[\#\*\_\s:]+$/, '')
        .replace(/[‘’]/g, "'")
        .toLowerCase();
    return SUBSECTION_LABELS.has(clean);
}
function convertBlock(node) {
    switch (node.type) {
        case 'heading': {
            const level = typeof node.attrs?.level === 'number' ? node.attrs.level : 1;
            const adjusted = Math.max(1, Math.min(6, level + headingLevelOffset));
            const runs = inlineRuns(node.content);
            const headingText = runs
                .map((r) => ('text' in r ? r.text : ''))
                .join('')
                .trim();
            const isUseCase = isUseCaseHeading(headingText);
            const isTravelSub = isSubsectionLabel(headingText);
            const isSubheading = adjusted >= 3 && !isUseCase;
            // Past the cap a sub-heading keeps the bold/upright label treatment but drops the brand
            // color, so it reads as a sub-heading OF its section rather than another section heading.
            const colorThisLevel = !!headingColor && (headingColorMaxLevel === 0 || adjusted <= headingColorMaxLevel);
            const boldThisLevel = headingBoldMaxLevel === 0 || adjusted <= headingBoldMaxLevel;
            const styledRuns = runs.map((r) => {
                if (!('text' in r))
                    return r;
                const out = { ...r };
                if (isUseCase) {
                    out.text = out.text.replace(/^[\*\_\s]+/, '').replace(/[\*\_\s]+$/, '');
                    out.bold = true;
                    out.underline = true;
                    out.italics = true;
                }
                else if (isTravelSub || isSubheading) {
                    // Sub-sub-header: a bold grey label at body size - visibly a level below
                    // the dark section sub-headings without adding another heading scale.
                    out.bold = true;
                    out.size = 21;
                    out.color = '475569';
                }
                else if (headingColor) {
                    if (colorThisLevel)
                        out.color = headingColor;
                    else if (headingColorAfterMaxLevel)
                        out.color = headingColorAfterMaxLevel;
                    out.bold = boldThisLevel;
                    if (!colorThisLevel && headingSizeAfterMaxLevel > 0) {
                        out.size = headingSizeAfterMaxLevel;
                    }
                }
                if (headingColor || headingShading)
                    out.italics = false;
                return out;
            });
            const para = {
                type: 'paragraph',
                heading: isTravelSub ? undefined : isUseCase ? 'Heading3' : (HEADING_BY_LEVEL[adjusted] ?? 'Heading2'),
                children: styledRuns,
                spacing: { before: 240, after: 120 },
            };
            if (sourceDocumentFormatting && isUserAcceptanceHeading(headingText)) {
                para.pageBreakBefore = true;
            }
            if (headingShading) {
                // Filled brand-color bar: pad it off the surrounding text and inset the label.
                para.shading = { fill: headingShading };
                para.spacing = { before: 80, after: 80 };
                para.indent = { left: 80 };
            }
            return [para];
        }
        case 'paragraph': {
            const runs = inlineRuns(node.content);
            const text = runs
                .map((r) => ('text' in r ? r.text : ''))
                .join('')
                .trim();
            if (sourceDocumentFormatting && isKnownSectionHeading(text)) {
                const styledRuns = runs.map((r) => {
                    if (!('text' in r))
                        return r;
                    const out = { ...r, bold: true };
                    if (headingColor)
                        out.color = headingColor;
                    return out;
                });
                return [
                    {
                        type: 'paragraph',
                        heading: 'Heading2',
                        children: styledRuns,
                        spacing: { before: 200, after: 120 },
                    },
                ];
            }
            if (sourceDocumentFormatting && isUseCaseHeading(text)) {
                const styledRuns = runs.map((r) => {
                    if (!('text' in r))
                        return r;
                    return { ...r, bold: true, underline: true, italics: true };
                });
                return [
                    {
                        type: 'paragraph',
                        heading: 'Heading3',
                        children: styledRuns,
                        spacing: { before: 200, after: 0 },
                    },
                ];
            }
            const isResult = /^(result|expected\s+result)\b/i.test(text);
            return [
                {
                    type: 'paragraph',
                    children: runs,
                    spacing: isResult
                        ? { before: 280, after: BODY_PARAGRAPH_SPACE_AFTER }
                        : { after: BODY_PARAGRAPH_SPACE_AFTER },
                },
            ];
        }
        case 'bulletList':
            return endListBlock(convertListItems(node, 'bullet', 0, ''));
        case 'orderedList': {
            ordinalCounter += 1;
            return endListBlock(convertListItems(node, 'ordered', 0, `ol-${ordinalCounter}`));
        }
        case 'blockquote':
            return (node.content ?? []).flatMap((child) => convertBlock(child));
        case 'codeBlock':
            return [
                {
                    type: 'paragraph',
                    children: inlineRuns(node.content).map((run) => 'text' in run ? { ...run, font: 'Courier New' } : run),
                },
            ];
        case 'table':
            return [convertTable(node)];
        case 'horizontalRule':
            return [];
        default:
            return Array.isArray(node.content)
                ? node.content.flatMap((child) => convertBlock(child))
                : [];
    }
}
/** Convert a Tiptap document object into contract DocumentChild nodes. */
export function tiptapToDocumentChildren(doc) {
    const content = Array.isArray(doc.content)
        ? doc.content
        : [];
    return content.flatMap((node) => convertBlock(node));
}
const SOURCE_BULLET = /^(\s*)[●○•■▪◦]\s*(.*)$/;
function isSourceDocumentMetadata(text) {
    return /^(request\s+for\s+approval|date|prepared\s+for|prepared\s+by)\s*:/i.test(text);
}
/**
 * Normalise only uploaded change-request text. The original text remains intact, but its
 * business structure becomes a document hierarchy that both renderers understand.
 */
function preprocessSourceDocument(raw) {
    const formattedRaw = (raw ?? '').replace(/(\S)\s*\n?\s*(RESULT\b|EXPECTED RESULT\b)/gi, '$1\n\n$2');
    const lines = formattedRaw.split(/\r?\n/);
    if (lines.some((line) => /^#{1,6}\s+/.test(line.trim())))
        return raw;
    const firstSection = lines.findIndex((line) => {
        const trimmed = line.trim();
        return isKnownSectionHeading(trimmed) || isUseCaseHeading(trimmed);
    });
    const titleCandidates = lines
        .map((line, index) => ({ line: line.trim(), index }))
        .filter(({ line, index }) => line.length > 0 &&
        (firstSection === -1 || index < firstSection) &&
        !SOURCE_BULLET.test(line) &&
        !isSourceDocumentMetadata(line));
    // Change requests commonly name the client and requested work as "Client - Work". Prefer
    // that explicit title pattern over an extraction-wrapped preamble sentence.
    const titleIndex = titleCandidates.filter(({ line }) => /\s-\s/.test(line)).at(-1)?.index ??
        // A title is usually a short label rather than a complete sentence. Prefer a short,
        // pre-section non-sentence line (not a conversational clause starting with 'when', 'the', etc.).
        titleCandidates.filter(({ line }) => line.length <= 70 &&
            !/[.!?]$/.test(line) &&
            !/^(when|the|if|as|a|an|for|in|on|with)\b/i.test(line)).at(-1)?.index;
    const processed = [];
    const titleText = titleIndex !== undefined ? lines[titleIndex].trim().toLowerCase() : '';
    if (titleIndex !== undefined)
        processed.push(`# ${lines[titleIndex].trim()}`, '');
    for (let index = 0; index < lines.length; index += 1) {
        if (index === titleIndex)
            continue;
        const line = lines[index] ?? '';
        const trimmed = line.trim();
        if (!trimmed) {
            processed.push('');
            continue;
        }
        if (titleText && (trimmed.toLowerCase() === titleText || (trimmed.length > 5 && titleText.includes(trimmed.toLowerCase())))) {
            continue;
        }
        if (isKnownSectionHeading(trimmed)) {
            processed.push('', `## ${trimmed}`, '');
            continue;
        }
        if (isUseCaseHeading(trimmed)) {
            processed.push('', `### ${trimmed}`, '');
            continue;
        }
        const bullet = SOURCE_BULLET.exec(line);
        if (bullet) {
            processed.push(`${bullet[1] ?? ''}- ${bullet[2] ?? ''}`.trimEnd());
            continue;
        }
        // PDF text extraction often places a wrapped bullet continuation on its own line.
        // Joining it to the preceding list item restores the one-bullet-per-requirement layout.
        const previous = processed.at(-1);
        if (previous?.trimStart().startsWith('- ') && previous.trim().length > 2) {
            processed[processed.length - 1] = `${previous} ${trimmed}`;
            continue;
        }
        processed.push(line);
    }
    return processed.join('\n');
}
/**
 * Convert a markdown (or plain text / Tiptap JSON) string into DocumentChild nodes.
 * `opts.headingLevelOffset` shifts every heading (e.g. -1 so a section's sub-headings
 * render one level below the section's own contract heading); default 0 (no shift).
 * `opts.headingColor` (hex or `{{token}}`) colors every heading run so narrative
 * sub-headings match the contract headings' brand color; default unset (inherit style).
 * `opts.headingShading` fills heading paragraphs (brand-color bars); `opts.tableHeaderFill`
 * / `opts.tableHeaderColor` style static markdown-table header cells; all default unset.
 */
export function markdownToDocumentChildren(markdown, opts) {
    const trimmed = (markdown ?? '').trim();
    if (!trimmed)
        return [];
    headingLevelOffset = opts?.headingLevelOffset ?? 0;
    headingColor = opts?.headingColor ?? '';
    headingColorMaxLevel = opts?.headingColorMaxLevel ?? 0;
    headingColorAfterMaxLevel = opts?.headingColorAfterMaxLevel ?? '';
    headingBoldMaxLevel = opts?.headingBoldMaxLevel ?? 0;
    headingSizeAfterMaxLevel = opts?.headingSizeAfterMaxLevel ?? 0;
    headingShading = opts?.headingShading ?? '';
    tableHeaderFill = opts?.tableHeaderFill ?? '';
    tableHeaderColor = opts?.tableHeaderColor ?? '';
    sourceDocumentFormatting = opts?.sourceDocument ?? false;
    try {
        return tiptapToDocumentChildren(parseContentToTiptap(sourceDocumentFormatting ? preprocessSourceDocument(trimmed) : trimmed));
    }
    finally {
        headingLevelOffset = 0;
        headingColor = '';
        headingColorMaxLevel = 0;
        headingColorAfterMaxLevel = '';
        headingBoldMaxLevel = 0;
        headingSizeAfterMaxLevel = 0;
        headingShading = '';
        tableHeaderFill = '';
        tableHeaderColor = '';
        sourceDocumentFormatting = false;
    }
}
//# sourceMappingURL=markdown-to-children.js.map